import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Item } from '@/types/item';
import { Modal } from '@/types/modal';
import Grid, { GridAction, itemHtml, itemList, itemText } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import ModalView from '@/components/modals/Modal';
import styles from '../../atoms/Section.module.scss';
import { useActionTracking } from '@/hooks/item';

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);

  const mutateTracking = useActionTracking(item.productId);

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('item.detail.actions.update_item_attribute'),
      onClick: () => setModal({ kind: 'UpdateItemAttribute', item, onClose: () => setModal(null) }),
      disabled: item.isSold !== '0',
    },
    {
      kind: 'Link',
      label: t('item.detail.actions.preview_link'),
      url: item.previewLink || '/',
      newTab: true,
      onClick: async () => {
        mutateTracking.mutate('product_preview');
      },
    },
  ];

  const getItemDescription = () => {
    switch ((item.language || '').toLowerCase()) {
      case 'de':
        return item.descriptionDe;
      case 'en':
        return item.descriptionEn;
      case 'es':
        return item.descriptionEs;
      case 'fr':
        return item.descriptionFr;
      case 'it':
        return item.descriptionIt;
      case 'ko':
        return item.descriptionKo;
      default:
        return item.description;
    }
  };

  const items = [
    itemText(t('item.detail.section'), item.sectionName),
    itemText(t('item.detail.brand'), item.brandName),
    itemText(t('item.detail.model'), item.model),
    itemText(t('item.detail.description'), getItemDescription(), {
      kind: 'Button',
      label: t('item.detail.detail_or_update'),
      onClick: () => setModal({ kind: 'UpdateItemDescription', item, onClose: () => setModal(null) }),
    }),
    itemText(t('item.detail.condition'), item.condition),
    itemText(t('item.detail.material'), item.material),
    itemText(t('item.detail.color'), item.color),
    itemText(t('item.detail.size'), item.size),
    itemList(t('item.detail.packaging'), item.packagings?.length ? item.packagings : null),
    itemHtml(
      t('item.detail.listing_curation_details'),
      <div className={styles.text}>
        {item.listingCurationDetails ? item.listingCurationDetails.replaceAll('. -', '.<br>-') : '-'}
      </div>,
    ),
    itemText(t('item.detail.tentative_country_of_origin'), item.countriesOfOrigin?.tentativeCoo),
    itemText(t('item.detail.real_country_of_origin'), item.countriesOfOrigin?.actualCoo),
  ];

  return (
    <>
      <Container
        title={t('item.detail.title')}
        actions={actions}
        testId="item_detail"
      >
        <Grid
          column={5}
          items={items}
        />
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
